import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import useAuthStore from '@store/authStore'
import { authApi } from '@api/auth'

/**
 * Hook per la gestione dell'autenticazione.
 * 
 * Fornisce:
 * - Stato autenticazione (user, isAuthenticated, isLoading)
 * - Azioni (login, logout, refreshProfile)
 * - Permessi (hasPermission, isAdmin)
 * 
 * Utilizza React Query per il caching e Zustand per lo stato globale.
 */
export const useAuth = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const {
    user,
    accessToken,
    refreshToken,
    isAuthenticated,
    setTokens,
    setUser,
    updateAccessToken,
    logout: storeLogout,
    hasPermission,
    isAdmin,
  } = useAuthStore()

  // Query per ottenere il profilo utente
  const {
    data: profileData,
    isLoading: isLoadingProfile,
    refetch: refetchProfile,
    isError: isProfileError,
  } = useQuery({
    queryKey: ['profile'],
    queryFn: () => authApi.getProfile().then(res => res.data),
    enabled: isAuthenticated && !!accessToken,
    staleTime: 5 * 60 * 1000, // 5 minuti
    retry: false,
    onSuccess: (data) => {
      setUser(data)
    },
  })

  // Gestione errore profilo - logout se token scaduto
  useEffect(() => {
    if (isProfileError && isAuthenticated && !profileData) {
      console.warn('Profilo non caricabile, logout automatico')
      storeLogout()
    }
  }, [isProfileError, isAuthenticated, profileData, storeLogout])

  // Mutation per il login
  const loginMutation = useMutation({
    mutationFn: ({ username, password }) => authApi.login(username, password),
    onSuccess: async (response) => {
      const { access, refresh } = response.data
      setTokens(access, refresh)
      
      // Carica il profilo utente
      try {
        const profileResponse = await authApi.getProfile()
        setUser(profileResponse.data)
        toast.success('Login effettuato con successo')
        navigate('/')
      } catch (error) {
        toast.error('Errore nel caricamento del profilo')
      }
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.detail || 'Credenziali non valide'
      toast.error(errorMessage)
    },
  })

  // Mutation per il cambio password
  const changePasswordMutation = useMutation({
    mutationFn: ({ oldPassword, newPassword, newPasswordConfirm }) => 
      authApi.changePassword(oldPassword, newPassword, newPasswordConfirm),
    onSuccess: () => {
      toast.success('Password aggiornata con successo')
    },
    onError: (error) => {
      const errors = error.response?.data
      if (errors) {
        Object.values(errors).forEach(err => {
          toast.error(Array.isArray(err) ? err[0] : err)
        })
      } else {
        toast.error('Errore durante il cambio password')
      }
    },
  })

  // Mutation per aggiornare il profilo
  const updateProfileMutation = useMutation({
    mutationFn: (data) => authApi.updateProfile(data),
    onSuccess: (response) => {
      setUser(response.data)
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      toast.success('Profilo aggiornato con successo')
    },
    onError: (error) => {
      const errors = error.response?.data
      if (errors) {
        Object.values(errors).forEach(err => {
          toast.error(Array.isArray(err) ? err[0] : err)
        })
      } else {
        toast.error('Errore durante l\'aggiornamento del profilo')
      }
    },
  })

  /**
   * Esegue il login.
   */
  const login = (username, password) => {
    loginMutation.mutate({ username, password })
  }

  /**
   * Esegue il logout.
   */
  const logout = () => {
    storeLogout()
    queryClient.clear()
    toast.info('Logout effettuato')
    navigate('/login')
  }

  /**
   * Cambia la password.
   */
  const changePassword = (oldPassword, newPassword, newPasswordConfirm) => {
    changePasswordMutation.mutate({ oldPassword, newPassword, newPasswordConfirm })
  }

  /**
   * Aggiorna il profilo.
   */
  const updateProfile = (data) => {
    updateProfileMutation.mutate(data)
  }

  // Carica il profilo all'avvio se autenticato
  useEffect(() => {
    if (isAuthenticated && accessToken && !user) {
      refetchProfile()
    }
  }, [isAuthenticated, accessToken, user, refetchProfile])

  // Usa i dati del profilo dalla query o dallo store
  const currentUser = profileData || user

  return {
    // Stato
    user: currentUser,
    isAuthenticated,
    isLoading: loginMutation.isPending || isLoadingProfile,
    
    // Azioni
    login,
    logout,
    changePassword,
    updateProfile,
    refetchProfile,
    
    // Permessi - usa currentUser invece di fare affidamento sullo store
    hasPermission: (permission) => {
      if (!currentUser) return false
      
      switch (permission) {
        case 'manage_orders':
          return currentUser.can_manage_orders
        case 'manage_inventory':
          return currentUser.can_manage_inventory
        case 'manage_products':
          return currentUser.can_manage_products
        case 'view_reports':
          return currentUser.can_view_reports
        default:
          return false
      }
    },
    isAdmin: currentUser?.ruolo === 'admin',
    
    // Stati mutation
    isLoggingIn: loginMutation.isPending,
    isChangingPassword: changePasswordMutation.isPending,
    isUpdatingProfile: updateProfileMutation.isPending,
  }
}

export default useAuth
